import React from 'react';

import { formatarQuantidade } from '../formatos';

interface Props {
  recebida: number;
  pedida: number;
  unidade: string;
  /** Na célula da tabela a barra é curta; no modal ela ocupa a largura do bloco. */
  largura?: number;
}

/**
 * Quanto da peça já chegou, como PROGRESSO.
 *
 * Nasceu de um defeito de leitura apontado pelo Victor em 21/09/2026: a coluna dizia
 * "2 de 4 UN" e ninguém sabia se 2 **chegaram** ou se 2 **faltavam**. Duas correções na
 * mesma peça — o verbo por extenso ("Chegaram 2 de 4") e a barra, que mostra a proporção
 * antes de qualquer leitura de texto.
 *
 * ⚠️ Cabe em UMA linha (regra do Victor, 12/08/2026): barra + texto lado a lado, com o
 * `title` da célula carregando a frase inteira. Estilo em `style` inline porque é layout
 * pontual e o catálogo (`13-catalogo-de-ui.md`) manda não criar CSS novo — não existe barra
 * de progresso no sistema hoje; se uma segunda tela precisar, aí vira `shared/ui/`.
 */
const BarraDeChegada: React.FC<Props> = ({ recebida, pedida, unidade, largura = 56 }) => {
  const proporcao = pedida > 0 ? Math.min(1, recebida / pedida) : 0;
  const faltam = Math.max(0, Number((pedida - recebida).toFixed(4)));
  const nadaChegou = recebida <= 0;

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
      <span
        aria-hidden="true"
        style={{
          width: largura,
          height: 6,
          flexShrink: 0,
          borderRadius: 3,
          background: 'var(--tema-fundo-2, #E4E4E4)',
          overflow: 'hidden',
        }}
      >
        <span
          style={{
            display: 'block',
            width: `${proporcao * 100}%`,
            height: '100%',
            background: '#EA5D0E',
            borderRadius: 3,
          }}
        />
      </span>
      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {nadaChegou ? (
          // Sem nenhuma chegada, "Chegaram 0 de 4" é ruído: o que importa é o que falta.
          <>
            {formatarQuantidade(pedida)} {unidade} a receber
          </>
        ) : (
          <>
            Chegaram <strong>{formatarQuantidade(recebida)}</strong> de {formatarQuantidade(pedida)} {unidade}
            <span style={{ color: '#6B7A99' }}> · faltam {formatarQuantidade(faltam)}</span>
          </>
        )}
      </span>
    </span>
  );
};

/** A mesma frase da barra, em texto puro — para o `title` da célula e a medição da coluna. */
export function textoDaChegada(recebida: number, pedida: number, unidade: string): string {
  if (recebida <= 0) return `${formatarQuantidade(pedida)} ${unidade} a receber`;
  const faltam = Math.max(0, Number((pedida - recebida).toFixed(4)));
  return (
    `Chegaram ${formatarQuantidade(recebida)} de ${formatarQuantidade(pedida)} ${unidade}` +
    ` · faltam ${formatarQuantidade(faltam)}`
  );
}

export default BarraDeChegada;
